/**
 * Tells the browser how tall the pinned patient header is, so that anything it scrolls into view
 * lands below the header rather than underneath it.
 *
 * Found in the browser pass on 2026-10-07. Every form on the patient screen opens with `autoFocus`
 * on its first field, and the browser scrolls a focused field into view **without knowing that
 * the top of the viewport is covered**: the identity block is `position: sticky`. The field landed
 * just below the block, so the form's heading, its explanation and the label of its first field
 * were hidden under it — on the allergy form, that included *"Den nuvarande versionen tas inte
 * bort…"*, the one sentence that says what saving does to a record with no `DELETE`.
 *
 * `scroll-padding-top` is what the browser consults for exactly this, but the block's height is not
 * a constant: it wraps on a narrow screen and grows with a second treatment limitation. So it is
 * measured, and published as `--pinned-height` on the root, which `index.css` adds to the banner's
 * height. One rule for all five forms and anything added later, rather than a scroll call in each.
 *
 * Used as a React 19 ref callback — the returned function is the cleanup, run when the block
 * unmounts, which removes the property so the ward list and the search are not padded for a header
 * they do not have.
 */
export function pinnedScrollPadding(element: HTMLElement | null): (() => void) | undefined {
  // jsdom has no ResizeObserver and no layout; there is nothing to measure there.
  if (element === null || typeof ResizeObserver === 'undefined') {
    return undefined
  }

  const root = document.documentElement

  const observer = new ResizeObserver(() => {
    root.style.setProperty('--pinned-height', `${element.offsetHeight}px`)
  })
  observer.observe(element)

  return () => {
    observer.disconnect()
    root.style.removeProperty('--pinned-height')
  }
}
